import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import StateMessage from "./StateMessage";
import "../pages/FlyersPage.css";

const EXCERPT_LEN = 140;

function excerpt(text) {
  if (!text) return "";
  const cleaned = text.replace(/\s+/g, " ").trim();
  if (cleaned.length <= EXCERPT_LEN) return cleaned;
  return `${cleaned.slice(0, EXCERPT_LEN).trim()}…`;
}

function formatPostDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("es-MX", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * News posts (and, on the news page, flyers).
 * - limit: show only the latest N posts
 * - compact: home-page variant with thumbnail-sized images
 */
export default function NewsFeed({ showFlyers = true, limit, compact = false }) {
  const [flyers, setFlyers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [expandedPostId, setExpandedPostId] = useState(null);
  const lastTriggerRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        let postsQuery = supabase
          .from("news_posts")
          .select(
            "id, title, body, image_url, image_orientation, published_at, created_at"
          )
          .eq("is_published", true)
          .order("published_at", { ascending: false });
        if (limit) postsQuery = postsQuery.limit(limit);

        const requests = [postsQuery];

        if (showFlyers) {
          requests.push(
            supabase
              .from("flyers")
              .select(
                "id, title, caption, image_url, sort_order, league_id, updated_at"
              )
              .eq("is_published", true)
              .order("sort_order", { ascending: true })
              .order("created_at", { ascending: false })
          );
        }

        const [postsRes, flyersRes] = await Promise.all(requests);

        if (postsRes.error) throw postsRes.error;
        if (flyersRes?.error) throw flyersRes.error;

        setPosts(postsRes.data || []);
        setFlyers(flyersRes?.data || []);
      } catch (e) {
        console.error(e);
        setErr("No se pudieron cargar las noticias.");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [showFlyers, limit]);

  useEffect(() => {
    if (!lightbox) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };

    document.body.style.overflow = "hidden";
    document.body.classList.add("overlay-open");
    window.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = "";
      document.body.classList.remove("overlay-open");
      window.removeEventListener("keydown", onKey);
      lastTriggerRef.current?.focus?.();
    };
  }, [lightbox]);

  const openLightbox = (event, src, title) => {
    lastTriggerRef.current = event.currentTarget;
    setLightbox({ src, title });
  };

  const empty = !loading && !err && posts.length === 0 && flyers.length === 0;
  const TitleTag = compact ? "h3" : "h2";

  return (
    <>
      <div className={`flyers-page${compact ? " flyers-page--compact" : ""}`}>
        {loading && (
          <div className="news-list" role="status" aria-label="Cargando noticias…">
            <span className="skeleton news-skeleton" />
          </div>
        )}
        {err && (
          <StateMessage kind="error" title={err}>
            Revisa tu conexión e intenta de nuevo.
          </StateMessage>
        )}
        {empty && (
          <StateMessage title="Sin noticias por ahora">
            Los avisos de la liga aparecerán aquí.
          </StateMessage>
        )}

        {!loading && !err && posts.length > 0 && (
          <section className="feed-section" aria-label="Noticias">
            <div className="news-list">
              {posts.map((post, index) => {
                const isOpen = expandedPostId === post.id;
                const dateLabel = formatPostDate(
                  post.published_at || post.created_at
                );
                const orientation =
                  post.image_orientation === "portrait"
                    ? "portrait"
                    : "landscape";
                const body = (post.body || "").trim();
                const hasMore = body.replace(/\s+/g, " ").length > EXCERPT_LEN;
                const bodyId = `news-body-${post.id}`;

                return (
                  <article
                    key={post.id}
                    className={[
                      "news-card",
                      isOpen ? "open" : "",
                      post.image_url
                        ? `news-card--${orientation}`
                        : "news-card--text",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    style={{ animationDelay: `${Math.min(index, 8) * 55}ms` }}
                  >
                    {post.image_url && (
                      <button
                        type="button"
                        className={`news-cover-wrap news-cover-wrap--${orientation}`}
                        aria-label={`Ampliar imagen: ${post.title}`}
                        onClick={(e) => openLightbox(e, post.image_url, post.title)}
                      >
                        <img
                          src={post.image_url}
                          alt=""
                          className={`news-cover news-cover--${orientation}`}
                          loading={index === 0 && !compact ? "eager" : "lazy"}
                          decoding="async"
                        />
                      </button>
                    )}
                    <div className="news-card-copy">
                      {dateLabel && (
                        <time
                          className="news-card-date"
                          dateTime={post.published_at || post.created_at}
                        >
                          {dateLabel}
                        </time>
                      )}
                      <TitleTag className="news-card-title">{post.title}</TitleTag>

                      {body && (
                        <div id={bodyId} className="news-card-text">
                          {isOpen || !hasMore ? (
                            <p className="news-body-text">{body}</p>
                          ) : (
                            <p className="news-card-excerpt">{excerpt(body)}</p>
                          )}
                        </div>
                      )}

                      {hasMore && (
                        <button
                          type="button"
                          className={`news-card-hint${isOpen ? " open" : ""}`}
                          aria-expanded={isOpen}
                          aria-controls={bodyId}
                          onClick={() =>
                            setExpandedPostId((id) =>
                              id === post.id ? null : post.id
                            )
                          }
                        >
                          {isOpen ? "Mostrar menos" : "Leer más"}
                          <svg
                            className="news-card-chevron"
                            viewBox="0 0 12 12"
                            width="10"
                            height="10"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M2.5 4.5 6 8l3.5-3.5"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {!loading && !err && showFlyers && flyers.length > 0 && (
          <section className="feed-section flyers-section" aria-labelledby="flyers-title">
            <div className="feed-section-heading">
              <h2 className="feed-section-title" id="flyers-title">
                Volantes
              </h2>
              <span className="feed-section-rule" aria-hidden="true" />
            </div>
            <ul className="flyers-grid">
              {flyers.map((flyer) => (
                <li key={flyer.id} className="flyer-item">
                  {flyer.image_url ? (
                    <button
                      type="button"
                      className="flyer-image-btn"
                      onClick={(e) =>
                        openLightbox(e, flyer.image_url, flyer.title || "Volante")
                      }
                      aria-label={
                        flyer.title ? `Ampliar volante: ${flyer.title}` : "Ampliar volante"
                      }
                    >
                      <img
                        src={flyer.image_url}
                        alt=""
                        className="flyer-image"
                        loading="lazy"
                        decoding="async"
                      />
                    </button>
                  ) : (
                    <div className="flyer-image-placeholder">Sin imagen</div>
                  )}
                  {(flyer.title || flyer.caption) && (
                    <div className="flyer-meta">
                      {flyer.title && <h3 className="flyer-title">{flyer.title}</h3>}
                      {flyer.caption && (
                        <p className="flyer-caption">{flyer.caption}</p>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {lightbox && (
        <div
          className="flyer-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.title || "Imagen"}
          onClick={() => setLightbox(null)}
        >
          <button
            ref={closeRef}
            type="button"
            className="flyer-lightbox-close"
            aria-label="Cerrar imagen"
            onClick={() => setLightbox(null)}
          >
            ×
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.title || "Imagen"}
            className="flyer-lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
